/* eslint-disable no-useless-escape, no-cond-assign */

import React from 'react';
import moment from 'moment';
import {
    STORAGEKEY_LOGIN_TOKEN, CAMPAIGN_DURATION_TYPES,
    POST_ATTACHMENT_VIDEO_EXTENSIONS, USER_TYPES,
    CLOUDIMAGE_URL, MIN_METATAG_IMAGE_SIZE,
    STORAGEKEY_CAMPAIGN_EXTERNAL_SUPPORT_IDS,
    SITE_URL, FACEBOOK_GRAPH_API, FACEBOOK_GRAPH_TOKEN,
    CAMPAIGN_ISNEW_DAYS_THRESHOLD, VALID_EMAIL_REGEX,
    STORAGEKEY_IS_PWA, REP_REQUEST_CONFIG,
    SHARE_SUB_TYPE_GROUP
} from 'config/constants';
import { format } from 'date-fns';
import { fr as dateFnsLocaleFr } from 'date-fns/locale';

/**
 * Create a deep copy of an object
 * @param {Object} obj
 * @returns {Object}
 */
export function deepCopyObject(obj) {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Save login token
 * @param {String} token
 * @param {Boolean} remember
 */
export function saveLoginToken(token, remember) {
    if (!remember) {
        sessionStorage.setItem(STORAGEKEY_LOGIN_TOKEN, token);
    }
    else {
        localStorage.setItem(STORAGEKEY_LOGIN_TOKEN, token);
    }
}

/**
 * Get login token
 * @returns {String}
 */
export function getLoginToken() {
    return localStorage.getItem(STORAGEKEY_LOGIN_TOKEN) || sessionStorage.getItem(STORAGEKEY_LOGIN_TOKEN);
}

/**
 * Delete login token
 */
export function deleteLoginToken() {
    localStorage.removeItem(STORAGEKEY_LOGIN_TOKEN);
    sessionStorage.removeItem(STORAGEKEY_LOGIN_TOKEN);
}

/**
 * Is the app opened in PWA mode ?
 */
export function setIsPWA() {
    localStorage.setItem(STORAGEKEY_IS_PWA, 'TRUE');
}

/**
 * Is the app opened in PWA mode ?
 * @returns {Boolean}
 */
export function isPWA() {
    return !!localStorage.getItem(STORAGEKEY_IS_PWA);
}

/**
 * Save the non registered user campaign support in the session storage
 * @param {Number} campaignId
 */
export function saveCampaignExternalSupport(campaignId) {
    let data = sessionStorage.getItem(STORAGEKEY_CAMPAIGN_EXTERNAL_SUPPORT_IDS);

    data = data ? JSON.parse(data) : [];
    data.push(campaignId);

    sessionStorage.setItem(
        STORAGEKEY_CAMPAIGN_EXTERNAL_SUPPORT_IDS,
        JSON.stringify(data)
    );
}

/**
 * Is the non registered user supporting this campaign already?
 * @param {Number} campaignId
 * @returns {Bool}
 */
export function isCampaignExternalSupport(campaignId) {
    let data = sessionStorage.getItem(STORAGEKEY_CAMPAIGN_EXTERNAL_SUPPORT_IDS);

    data = data ? JSON.parse(data) : [];

    return data.indexOf(campaignId) > -1;
}

/**
 * Make a Promise "cancelable"
 * @param {Promise} promise
 * @param {Object} rejectProps
 * @param {Bool} isCanceled
 * @returns {Object}
 */
export function cancelablePromise(promise, rejectProps = {}, isCanceled = false) {
    return {
        promise: new Promise((resolve, reject) => {
            promise.then(
                data => isCanceled ? reject({ isCanceled, ...rejectProps }) : resolve(data),
                err => isCanceled ? reject({ isCanceled, ...rejectProps }) : reject(err)
            );
        }),
        cancel: (props) => {
            rejectProps = props;
            isCanceled = true;
        }
    };
}

/**
 * ellipsifies a string when exceeding the given max length
 * @param {String} string
 * @param {Number} maxLength
 * @returns {String}
 */
export function ellipsisString(string, maxLength) {
    if (!string) {
        return '';
    }

    return typeof string === 'string' && string.length > (maxLength - 3)
        ? `${string.substr(0, maxLength).trim()}...`
        : string;
}

/**
 * get the campaigns remaining days data
 * @param {Number} durationTypeId
 * @param {String} creationDateString
 * @returns {Object}
 */
export function getCampaignRemainingDays(durationTypeId, creationDateString) {
    const durationData = CAMPAIGN_DURATION_TYPES.find((type) => type.id === durationTypeId) || {};
    const remainingDays = moment(creationDateString).clone().add(durationData.days, 'd').diff(moment().startOf('day'), 'days');

    return {
        text: remainingDays > 1
            ? `${remainingDays} jours restants!`
            : remainingDays === 1
                ? '1 jour restant!'
                : remainingDays === 0
                    ? 'Se termine aujourd\'hui!'
                    : 'Campagne terminée',
        days: remainingDays
    };
}

/**
 * get the rep's job name by user role_type and gender
 * @param {Number} roleType
 * @param {String} gender
 * @returns {String}
 */
export function getRepJobName(roleType, gender) {
    switch (roleType) {
        case USER_TYPES.DEPUTE:
            return gender === 'M' ? 'Député' : 'Députée';

        case USER_TYPES.SENATEUR:
            return gender === 'M' ? 'Sénateur' : 'Sénatrice';

        case USER_TYPES.CANDIDATE:
            return gender === 'M' ? 'Candidat' : 'Candidate';

        default:
            return '';
    }
}

/**
 * get a user's concated name by data
 * @param {Object} data
 * @returns {String}
 */
export function getUserName(data) {
    if (!data) {
        return '';
    }

    const { is_organization, first_name, last_name } = data;

    return is_organization || !last_name
        ? first_name
        : `${first_name} ${last_name.slice(0, 1)}.`;
}

/**
 * Check if the provided url includes a supported video extension
 * @param {String} url
 * @returns {Bool}
 */
export function isVideoUrl(url) {
    let isVideo = false;

    url = url.toLowerCase();

    POST_ATTACHMENT_VIDEO_EXTENSIONS.find((ext) => {
        if (url.indexOf(ext) > -1) {
            isVideo = true;

            return true;
        }

        return false;
    });

    return isVideo;
}

/**
 * Split numbers by space (1000x)
 * @param {Number} number
 * @returns {String}
 */
export function numberWithSpaces(number) {
    return `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Async read a file (blob) and return it's data
 * @param {Object} file
 * @returns {Promise}
 */
export function readFile(file) {
    return new Promise((resolve) => {
        const fileReader = new FileReader();

        fileReader.addEventListener('load', resolve, false);
        fileReader.readAsDataURL(file);
    });
}

/**
 * Async read a image url and return it's data
 * @param {String} imageUrl
 * @returns {Promise}
 */
export function readImageUrl(imageUrl) {
    return new Promise((resolve) => {
        const image = new Image();

        image.addEventListener('load', resolve, false);
        image.src = imageUrl;
    });
}

/**
 * Return a cloud image CDN url only
 * @param {String} imageUrl
 * @returns {String}
 */
export function cloudImageCDN(imageUrl) {
    return `${CLOUDIMAGE_URL}cdn/n/n/${imageUrl}`;
}

/**
 * Return a cloud image url: CROP
 * @param {String} imageUrl
 * @param {Number} width
 * @param {Number} height
 * @returns {String}
 */
export function cloudImageCrop(imageUrl, width, height = width) {
    return `${CLOUDIMAGE_URL}crop/${width}x${height}/n/${imageUrl}`;
}

/**
 * Return a cloud image url: FIT
 * @param {String} imageUrl
 * @param {Number} width
 * @param {Number} height
 * @param {String} filter see config/constants.js -> CLOUDIMAGE_FILTERS
 * @returns {String}
 */
export function cloudImageFit(imageUrl, width, height = width, filter = 'n') {
    return `${CLOUDIMAGE_URL}fit/${width}x${height}/${filter}/${imageUrl}`;
}

/**
 * Return a cloud image url: BOUND
 * @param {String} imageUrl
 * @param {Number} width
 * @param {Number} height
 * @returns {String}
 */
export function cloudImageBound(imageUrl, width, height = width) {
    return `${CLOUDIMAGE_URL}bound/${width}x${height}/n/${imageUrl}`;
}

/**
 * Return a cloud image url: TO WIDTH (keep ratio)
 * @param {String} imageUrl
 * @param {Number} width
 * @returns {String}
 */
export function cloudImageToWidth(imageUrl, width) {
    return `${CLOUDIMAGE_URL}width/${width}/n/${imageUrl}`;
}

/**
 * Return a cloud image url: TO HEIGHT (keep ratio)
 * @param {String} imageUrl
 * @param {Number} height
 * @returns {String}
 */
export function cloudImageToHeight(imageUrl, height) {
    return `${CLOUDIMAGE_URL}height/${height}/n/${imageUrl}`;
}

/**
 * Checks if the input string is a valid email format
 * @param {String} email
 * @returns {Boolean}
 */
export function isValidEmail(email) {
    return VALID_EMAIL_REGEX.test(`${email}`.toLowerCase());
}

/**
 * return parsed image data props for the metatags having a minimum image size
 * @param {Object} data
 * @returns {Object}
 */
export function getImageMetaTagProps(data) {
    let image = data.image;
    let width = data.width;
    let height = data.height;

    if (width && height && (width < MIN_METATAG_IMAGE_SIZE || height < MIN_METATAG_IMAGE_SIZE)) {
        const ratio = width / height;

        if (width < height) {
            image = cloudImageToWidth(image, MIN_METATAG_IMAGE_SIZE);
            width = MIN_METATAG_IMAGE_SIZE;
            height = Math.round(MIN_METATAG_IMAGE_SIZE / ratio);
        }
        else {
            image = cloudImageToHeight(image, MIN_METATAG_IMAGE_SIZE);
            height = MIN_METATAG_IMAGE_SIZE;
            width = Math.round(MIN_METATAG_IMAGE_SIZE * ratio);
        }
    }
    else {
        image = cloudImageCDN(image);
    }

    return {
        image: image,
        imageWidth: height,
        imageHeight: width
    };
}

/**
 * parse text to JSX while keeping linebreaks
 * @param {String} text
 * @returns {Elements}
 */
export function printWithNewlines(text) {
    if (!text) {
        return '';
    }

    const newLines = text.split('\n');
    const newLineCount = newLines.length - 1;
    const mapNewLine = (paragraph, index) => (
        <span key={index}>
            {paragraph}
            {index < newLineCount
                ? <br />
                : null
            }
        </span>
    );

    return newLines.map(mapNewLine);
}

/**
 * Extract a youtube video hash from the given string
 * @param {String} string
 * @param {Boolean} isHashOnly
 * @returns {String} youtube embed url or hash
 */
export function getYoutubeVideoEmbedUrl(string, isHashOnly = false) {
    if (!string) {
        return '';
    }

    let match = string.match(/.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#\&\?]*).*/);

    if (match) {
        match[1] = match[1].trim();

        const whiteSpaceIndex = match[1].indexOf(' ');

        if (whiteSpaceIndex > -1) {
            match[1] = match[1].substr(0, whiteSpaceIndex);
        }

        const videoHash = (match[1].length === 11) ? match[1] : '';

        return (isHashOnly || !videoHash) ? videoHash : `https://www.youtube.com/embed/${videoHash}`;
    }

    return '';
}

/**
 * Request facebook graph api to re-scrape the given url
 * @param {String} route
 * @returns {Boolean | Object}
 */
export async function requestFacebookScrape(route) {
    try {
        const ID = encodeURIComponent(`${SITE_URL.slice(0, -1)}${route}`);
        const response = await fetch(`${FACEBOOK_GRAPH_API}?id=${ID}&access_token=${FACEBOOK_GRAPH_TOKEN}&scrape=true`, {
            method: 'POST',
            headers: {
                Accept: 'application/x-www-form-urlencoded',
                mode: 'cors'
            }
        });

        return response;
    }
    catch (err) {
        // console.warn(err);

        return false;
    }
}

/**
 * Checks if the campaign is new by a fixed threshold
 * @param {String} creationDate
 * @returns {Boolean}
 */
export function isCampaignNew(creationDate) {
    return moment(creationDate).add(CAMPAIGN_ISNEW_DAYS_THRESHOLD, 'days').isSameOrAfter(moment());
}

/**
 * Get the postal information from a user's profile data object
 * @param {Object} data
 * @returns {Object}
 */
export function getPostalDataFromProfileData(data) {
    let postalCode;
    let postalRegion;

    try {
        if (data) {
            postalCode = data.code;

            if (data.rep_zone && data.rep_zone.subregion) {
                postalRegion = data.rep_zone.subregion.name;
            }
        }
    }
    catch (err) {
        // err
    }

    return {
        postalCode: postalCode || '',
        postalRegion: postalRegion || ''
    };
}

/**
 * Get the total amount of campaign supporters by campaign data
 * @param {Object} campaignData
 * @returns {Number}
 */
export function calcTotalCampaignSupporters(campaignData) {
    const { users_count, users, extrenal_supports_count } = campaignData;
    const internalSupporters = users_count || (users ? users.length : 0);

    return internalSupporters + extrenal_supports_count;
}

/**
 * Sort by name
 * @param {Object} a
 * @param {Object} b
 * @returns {Number}
 */
export function sortByName(a, b) {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    if (nameA < nameB) {
        return -1;
    }

    if (nameA > nameB) {
        return 1;
    }

    return 0;
}

/**
 * Camel Case a string
 * @param {String} string
 * @returns {String}
 */
export function camelizeString(string) {
    const mapWord = (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

    return string.split(' ').map(mapWord).join(' ');
}

/**
 * get the rep config response by a rep response
 * @param {String} repResponse
 * @param {Bool} isReturnConfig return the config object
 * @returns {String}
 */
export function getFirstRepResponse(repResponse, isReturnConfig = false) {
    repResponse = repResponse || '';

    const firstResponseId = parseInt(repResponse.split(',')[0], 10);
    const findItem = (item) => item.id === firstResponseId;
    const itemConfig = REP_REQUEST_CONFIG.find(findItem) || {};

    if (isReturnConfig) {
        return itemConfig;
    }

    return itemConfig.response || '';
}

/**
 * Get rep response info
 * @param {String} response
 * @returns {String}
 */
export function getRepResponseInfo(response) {
    response = response || '';

    const mapParseId = (id) => parseInt(id, 10);
    const responseIds = response.split(',').map(mapParseId);

    if (responseIds.length === 1) {
        const findItem = ({ id }) => id === responseIds[0];
        const { response } = REP_REQUEST_CONFIG.find(findItem) || {};

        return response || '';
    }

    return `${responseIds.length} engagements pris !`;
}

/**
 * Get rep response info
 * @param {String} response
 * @param {Bool} isReturnObjects return the config objects
 * @returns {String}
 */
export function getRepResponses(response, isReturnObjects = false) {
    response = response || '';

    const mapResponse = (id) => {
        id = parseInt(id, 10);

        const findItem = ({ id: _id }) => _id === id;
        const config = REP_REQUEST_CONFIG.find(findItem) || {};

        return isReturnObjects ? config : config.response;
    };

    return response.split(',').map(mapResponse);
}

/**
 * Filter reps by campaign type
 * @param {Array} reps
 * @param {Number} campaignType
 * @returns {Array}
 */
export function filterRepsByCampaignType(reps, campaignType) {
    switch (campaignType) {
        case 1: {
            const filterRep = ({ role_type }) => role_type === USER_TYPES.CANDIDATE;

            return reps.filter(filterRep);
        }

        case 2: {
            const filterRep = ({ role_type }) => (
                role_type === USER_TYPES.DEPUTE ||
                role_type === USER_TYPES.SENATEUR
            );

            return reps.filter(filterRep);
        }

        default:
            return reps;
    }
}

/**
 * Randomly shuffle an array
 * @param {Array} array
 * @returns {Array}
 */
export function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));

        [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
}

/**
 * Mapping function for getCampaignStats api -> "supports" list
 * @param {Array} supports
 * @returns {Array}
 */
export function mapCampaignStatsSupports(supports) {
    const mapId = (id) => parseInt(id, 10);
    const findShareSocial = (type) => SHARE_SUB_TYPE_GROUP.SOCIAL.indexOf(type) > -1;
    const findShareFriends = (type) => SHARE_SUB_TYPE_GROUP.FRIENDS.indexOf(type) > -1;

    let { share_type: shareTypes } = supports;

    shareTypes = `${shareTypes || ''}`.split(',').map(mapId);

    return {
        ...supports,
        isSharedSocial: !!shareTypes.find(findShareSocial),
        isSharedFriends: !!shareTypes.find(findShareFriends)
    };
}

/**
 * Triggers to download the campaign supporters list as .csv
 * @param {Array} items campaign supporter list
 */
export function downloadCampaignSupportList(items) {
    const headerRow = [
        {
            key: 'email',
            name: 'Email'
        }, {
            key: 'first_name',
            name: 'Nom'
        }, {
            key: 'last_name',
            name: 'Prénom'
        }, {
            key: 'region',
            name: 'Region'
        }, {
            key: 'postal_code',
            name: 'Code postal'
        }, {
            key: 'is_requested',
            name: 'A alerté les élus'
        }, {
            key: 'is_agree',
            name: 'Recontact autorisé'
        }, {
            key: 'isSharedSocial',
            name: 'Partage à un proche'
        }, {
            key: 'isSharedFriends',
            name: 'Partage via post'
        }
    ];

    const replacerValue = (key, value) => !value ? '' : value;

    const mapItemRow = (item) => {
        const mapCol = ({ key }) => {
            let data;

            switch (key) {
                case 'region': {
                    const value = item.subregion
                        ? item.subregion.name
                        : item.user_subregion
                            ? item.user_subregion.name
                            : '';

                    data = JSON.stringify(value, replacerValue);

                    break;
                }

                case 'postal_code': {
                    const value = item.subregion
                        ? item.subregion.code
                        : item.user_subregion
                            ? item.user_subregion.code
                            : '';

                    data = JSON.stringify(value, replacerValue);

                    break;
                }

                case 'is_agree': {
                    data = JSON.stringify(item.is_agree ? 'OUI' : 'NON', replacerValue);

                    break;
                }

                case 'is_requested': {
                    data = JSON.stringify(item.is_requested ? 'OUI' : 'NON', replacerValue);

                    break;
                }

                case 'isSharedSocial': {
                    data = JSON.stringify(item.isSharedSocial ? 'OUI' : 'NON', replacerValue);

                    break;
                }

                case 'isSharedFriends': {
                    data = JSON.stringify(item.isSharedFriends ? 'OUI' : 'NON', replacerValue);

                    break;
                }

                default: {
                    data = JSON.stringify(item[key], replacerValue);

                    break;
                }
            }

            return data;
        };

        return headerRow.map(mapCol).join(',');
    };

    const mapHeaderRow = ({ name }) => name;

    let csv = items.map(mapItemRow);

    csv.unshift(headerRow.map(mapHeaderRow));
    csv = csv.join('\r\n');

    const link = document.createElement('a');

    link.href = 'data:text/xls;charset=utf-8,' + encodeURI(csv);
    link.style = 'visibility: hidden; height: 0; width: 0';
    link.download = `liste-des-soutiens_${moment().format('MM-DD-YYYY')}.xls`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * date-fns wrapper for formatting
 * @param {Object} date
 * @param {String} formatString
 * @param {Object} options
 * @returns {String}
 */
export function formatDate(date, formatString, options = {}) {
    return format(date, formatString, {
        locale: dateFnsLocaleFr,
        ...options
    });
}
